import type { Locale } from "./i18n";
import type { LauncherFollower } from "./launcher-anchor";
import type { FabPosition } from "./types";

import announcementsCss from "./announcements.css?inline";
import { setLanguage, t } from "./i18n";
import {
	computeLauncherAnchor,
	createLauncherFollower,
} from "./launcher-anchor";
import { attachStyles } from "./styles";

/**
 * One announcement as the `weloop:announcements` message carries it: what
 * the widget's home card shows, as plain values. The rich body never enters
 * the host page DOM – the lead is text and the picture a URL.
 */
export interface AnnouncementChip {
	readonly id: string;
	readonly imageUrl?: string;
	readonly kind: string;
	readonly lead?: string;
	readonly requireAcknowledge?: boolean;
	readonly title: string;
}

export interface AnnouncementSurfaceHandle {
	readonly dispose: () => void;
	readonly hide: () => void;
	readonly show: (item: AnnouncementChip) => void;
}

/** The corner card follows the launcher; the banner stays docked on top. */
export type AnnouncementChipHandle = AnnouncementSurfaceHandle &
	LauncherFollower;

interface SurfaceCallbacks {
	/** "Got it" on an announcement that asks for a confirmation. */
	readonly onAcknowledge?: (id: string) => void;
	readonly onDismiss: (id: string) => void;
	readonly onOpen: (id: string | undefined) => void;
}

/**
 * Category label for the banner badge – every kind the dashboard can put on
 * the banner, so the badge matches the one the placement preview shows. A
 * kind a newer widget build introduced has no label to render, and the badge
 * collapses rather than showing a wrong one.
 */
const kindLabel = (locale: Locale, kind: string): string => {
	if (kind === "service_status") {
		return t(locale, "announcement_kind_service_status");
	}
	if (kind === "business_update") {
		return t(locale, "announcement_kind_business_update");
	}
	if (kind === "improvement") {
		return t(locale, "announcement_kind_improvement");
	}
	if (kind === "fix") {
		return t(locale, "announcement_kind_fix");
	}
	if (kind === "new") {
		return t(locale, "announcement_kind_new");
	}
	if (kind === "resource") {
		return t(locale, "announcement_kind_resource");
	}
	if (kind === "product_update") {
		return t(locale, "announcement_kind_product_update");
	}
	return "";
};

/**
 * Reserve room at the top of the host page for the docked banner, the way a
 * cookie bar does: the page shifts down by the bar's height instead of being
 * covered by it. A host header that is itself `position: fixed` keeps its own
 * placement and stays behind the banner – nothing outside normal flow can be
 * moved without rewriting the customer's layout.
 */
const createPageOffset = () => {
	let applied = false;
	// Inline margin the host page carried before the banner claimed the top
	// edge, restored verbatim once the banner goes away.
	let previousInline = "";
	// Margin the page computes on its own, which the bar height adds to.
	let base = 0;

	return {
		clear: (): void => {
			if (!applied) {
				return;
			}
			document.body.style.marginTop = previousInline;
			applied = false;
		},
		set: (height: number): void => {
			if (!applied) {
				previousInline = document.body.style.marginTop;
				const computed = Number.parseFloat(
					window.getComputedStyle(document.body).marginTop,
				);
				base = Number.isFinite(computed) ? computed : 0;
				applied = true;
			}
			document.body.style.marginTop = `${base + height}px`;
		},
	};
};

/**
 * Track the banner's rendered height so the page offset follows it – the
 * badge drops out on narrow viewports, which changes the bar's height.
 * `offsetHeight` rather than the observed box: it ignores the entrance
 * transform, so the offset is right from the first frame.
 */
const observeHeight = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLElement is an external mutable DOM interface
	element: HTMLElement,
	onHeight: (height: number) => void,
): (() => void) => {
	onHeight(element.offsetHeight);
	if (typeof ResizeObserver === "undefined") {
		return () => {
			// Nothing observed, nothing to tear down.
		};
	}
	const observer = new ResizeObserver(() => {
		const { offsetHeight } = element;
		if (offsetHeight > 0) {
			onHeight(offsetHeight);
		}
	});
	observer.observe(element);
	return () => {
		observer.disconnect();
	};
};

const mountSurface = (
	surface: "banner" | "chip",
	hostId: string,
	locale: Locale,
	position: FabPosition,
	callbacks: SurfaceCallbacks,
	accentColor?: string,
): AnnouncementChipHandle => {
	const host = document.createElement("div");
	host.id = hostId;
	host.dataset["surface"] = surface;
	host.dataset["position"] = position;
	// The project's main color crosses into the shadow root as a custom
	// property on the host, the way the launcher takes its colors in `fab.ts`.
	if (accentColor !== undefined) {
		host.style.setProperty("--accent-read", accentColor);
	}
	host.toggleAttribute("data-hidden", true);
	const shadow = host.attachShadow({ mode: "closed" });

	attachStyles(shadow, announcementsCss);

	const root = document.createElement("div");
	root.className = surface;
	const title = document.createElement("span");
	title.className = "title";
	// The chip holds its own Read, Got it and dismiss buttons, so the root is
	// a named group – a button role would hide those buttons from assistive
	// tech. The id is scoped to this shadow root, so it cannot collide.
	title.id = "title";
	root.setAttribute("role", surface === "banner" ? "status" : "group");
	if (surface === "chip") {
		root.setAttribute("aria-labelledby", "title");
	}

	const dismiss = document.createElement("button");
	dismiss.className = "dismiss";
	dismiss.type = "button";
	dismiss.setAttribute("aria-label", t(locale, "announcement_dismiss"));
	// Inline multiplication sign as the close glyph – an SVG icon would cost
	// bundle bytes for no legibility gain at this size.
	dismiss.textContent = "×";

	let current: AnnouncementChip | undefined;
	// Chip only – the banner stays a one-line band.
	let lead: HTMLParagraphElement | undefined;
	let picture: HTMLImageElement | undefined;
	// The category label, coloured like the widget's own kind chip so the
	// corner chip reads the same as the home card behind it. Empty (and
	// hidden) for an unlisted host, which only gets the availability label.
	const badge = document.createElement("span");
	badge.className = "badge";
	// Banner only – the corner chip floats and leaves the page alone.
	const pageOffset = surface === "banner" ? createPageOffset() : undefined;
	let stopObservingHeight: (() => void) | undefined;
	const releasePage = (): void => {
		stopObservingHeight?.();
		stopObservingHeight = undefined;
		pageOffset?.clear();
	};

	// "Got it" on either surface, only for an announcement that asks for a
	// confirmation (the dashboard's "Require a Got it"): hide at once, then
	// let the widget record the acknowledgment and re-post the surfaces,
	// which fills the slot with the runner-up if any. The others only carry
	// the close cross, which dismisses.
	const gotIt = document.createElement("button");
	gotIt.className = "got-it";
	gotIt.type = "button";
	gotIt.textContent = t(locale, "announcement_got_it");
	gotIt.addEventListener("click", (event) => {
		event.stopPropagation();
		if (current === undefined) {
			return;
		}
		releasePage();
		host.toggleAttribute("data-hidden", true);
		(callbacks.onAcknowledge ?? callbacks.onDismiss)(current.id);
	});

	if (surface === "banner") {
		const sheen = document.createElement("span");
		sheen.className = "sheen";
		sheen.setAttribute("aria-hidden", "true");

		const view = document.createElement("button");
		view.className = "view";
		view.type = "button";
		view.textContent = t(locale, "announcement_view");
		view.addEventListener("click", () => {
			callbacks.onOpen(current?.id);
		});
		root.append(sheen, badge, title, view, gotIt, dismiss);
	} else {
		// The corner card mirrors the home block: badge, title, lead, picture,
		// then "Read" and "Got it" – the same content the viewer would meet by
		// opening the widget.
		const head = document.createElement("div");
		head.className = "head";
		head.append(badge, dismiss);

		lead = document.createElement("p");
		lead.className = "lead";

		picture = document.createElement("img");
		picture.className = "picture";
		picture.alt = "";
		picture.loading = "lazy";
		picture.decoding = "async";

		const actions = document.createElement("div");
		actions.className = "actions";

		const read = document.createElement("button");
		read.className = "read";
		read.type = "button";
		read.textContent = t(locale, "announcement_read");
		read.addEventListener("click", () => {
			callbacks.onOpen(current?.id);
		});

		actions.append(read, gotIt);
		root.append(head, title, lead, picture, actions);
	}

	dismiss.addEventListener("click", (event) => {
		event.stopPropagation();
		if (current !== undefined) {
			// Hide immediately – persistence runs through the widget iframe and
			// the next surfaces settle keeps the slot empty (or reveals the
			// runner-up announcement).
			releasePage();
			host.toggleAttribute("data-hidden", true);
			callbacks.onDismiss(current.id);
		}
	});

	shadow.append(root);
	document.body.append(host);

	// The corner card pins next to a moved launcher. The banner never moves –
	// its follower is never fed a launcher, so it keeps the stylesheet spot.
	const follower = createLauncherFollower(host, {
		anchorFor: computeLauncherAnchor,
		fallbackSide: position,
	});
	// A picture that arrives after `show` makes the card taller; re-pin so it
	// still fits on the side it was placed.
	picture?.addEventListener("load", follower.place);

	return {
		dispose: () => {
			picture?.removeEventListener("load", follower.place);
			releasePage();
			host.remove();
		},
		hide: () => {
			releasePage();
			host.toggleAttribute("data-hidden", true);
		},
		setLauncher: follower.setLauncher,
		setLauncherMoving: follower.setLauncherMoving,
		show: (item) => {
			current = item;
			root.dataset["kind"] = item.kind;
			title.textContent = item.title;
			badge.textContent = kindLabel(locale, item.kind);
			setLanguage(host, locale);
			gotIt.toggleAttribute("data-hidden", item.requireAcknowledge !== true);
			if (lead !== undefined) {
				lead.textContent = item.lead ?? "";
			}
			if (picture !== undefined) {
				const imageUrl = item.imageUrl ?? "";
				picture.toggleAttribute("data-hidden", imageUrl === "");
				// Clear rather than keep a stale picture when the next
				// announcement has none.
				picture.src = imageUrl;
			}
			host.toggleAttribute("data-hidden", false);
			// Placed in the same task it became visible, so the browser never
			// paints it at the old spot.
			follower.place();
			// Measured after the host is visible – a hidden element is 0 tall.
			if (pageOffset !== undefined) {
				stopObservingHeight ??= observeHeight(host, pageOffset.set);
			}
		},
	};
};

/**
 * Top-docked host-page banner for an announcement targeted at this page.
 * Full-bleed brand band, coloured by the announcement's category, that pushes
 * the host page down instead of floating over it. Closed shadow root like the
 * FAB; the title renders through `textContent` so admin-authored content can
 * never inject markup into the host page.
 */
export const mountAnnouncementBanner = (
	locale: Locale,
	callbacks: SurfaceCallbacks,
): AnnouncementSurfaceHandle =>
	mountSurface(
		"banner",
		"weloop-announcement-banner",
		locale,
		"right",
		callbacks,
	);

/**
 * Announcement chip above the launcher bubble. Shares the corner slot with
 * the unread pill – the caller arbitrates which one draws.
 */
export const mountAnnouncementChip = (
	locale: Locale,
	position: FabPosition,
	callbacks: SurfaceCallbacks,
	// The project's main color; the Read button takes it. Absent keeps the
	// chip's own ink.
	primaryColor?: string,
): AnnouncementChipHandle =>
	mountSurface(
		"chip",
		"weloop-announcement-chip",
		locale,
		position,
		callbacks,
		primaryColor,
	);
